import {
  MINIMUM_VIDEO_COMPARISON_SELECTIONS,
  VideoSelectionUrlError,
  parseVideoSelectionUrl,
  serializeVideoSelectionUrl,
  type VideoSelection,
  type VideoSelectionUrlState,
} from "@meta-video/contracts";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  notifySameDocumentSearchChange,
  subscribeToLocationSearchChanges,
} from "../../shared/url/locationSearch.js";
import {
  clearComparisonUrl,
  parseComparisonViewSearch,
  rawSearch,
  removeComparisonView,
  removeVideoDetailView,
} from "../../shared/url/comparisonSearch.js";
import { parseVideoWorkspaceSearch } from "../../shared/url/workspaceSearch.js";

export { clearComparisonUrl } from "../../shared/url/comparisonSearch.js";

const HISTORY_STATE_KEY = "__metaVideoComparison";
const OPEN_HISTORY_STATE = "open";
const CLOSED_HISTORY_STATE = "closed";
const EMPTY_VIDEO_SELECTIONS: VideoSelectionUrlState = Object.freeze([]);

type ComparisonHistoryState = Record<string, unknown> & {
  readonly [HISTORY_STATE_KEY]?: typeof OPEN_HISTORY_STATE | typeof CLOSED_HISTORY_STATE;
};

export interface ComparisonUrlState {
  readonly clearSelections: () => void;
  readonly closeComparison: () => void;
  readonly error: string | null;
  readonly isOpen: boolean;
  readonly openComparison: () => void;
  readonly selections: VideoSelectionUrlState;
  readonly setSelections: (selections: readonly VideoSelection[]) => boolean;
}

function stateWithoutComparisonMarker(): ComparisonHistoryState {
  const current = window.history.state;
  if (current === null || typeof current !== "object") return {};
  const next = { ...(current as Record<string, unknown>) };
  delete next[HISTORY_STATE_KEY];
  return next;
}

function pathWithSearch(search: string): string {
  return `${window.location.pathname}${search === "" ? "" : `?${search}`}${window.location.hash}`;
}

export function useComparisonUrlState(): ComparisonUrlState {
  const [search, setSearch] = useState(() => window.location.search);
  const [writeError, setWriteError] = useState<string | null>(null);
  const closingWithBackRef = useRef(false);

  useEffect(() => {
    const onLocationSearchChange = () => {
      closingWithBackRef.current = false;
      setWriteError(null);
      setSearch(window.location.search);
    };
    return subscribeToLocationSearchChanges(onLocationSearchChange);
  }, []);

  const parsedSelection = useMemo(() => parseVideoSelectionUrl(search), [search]);
  const parsedView = useMemo(() => parseComparisonViewSearch(search), [search]);
  const parsedVideo = useMemo(() => parseVideoWorkspaceSearch(search), [search]);
  const selections = parsedSelection.kind === "valid" && parsedView.kind !== "invalid"
    ? parsedSelection.selections
    : EMPTY_VIDEO_SELECTIONS;
  const error = writeError
    ?? (parsedSelection.kind === "invalid" ? parsedSelection.message : null)
    ?? (parsedView.kind === "invalid" ? parsedView.message : null);
  const isOpen = error === null
    && parsedView.kind === "valid"
    && parsedVideo.kind === "valid"
    && !parsedVideo.videoView
    && selections.length >= MINIMUM_VIDEO_COMPARISON_SELECTIONS;
  const hasInsufficientOpenSelection = parsedSelection.kind !== "invalid"
    && parsedView.kind === "valid"
    && (parsedSelection.kind === "absent"
      || parsedSelection.selections.length < MINIMUM_VIDEO_COMPARISON_SELECTIONS);

  const replaceSearch = useCallback((nextSearch: string) => {
    closingWithBackRef.current = false;
    window.history.replaceState(
      stateWithoutComparisonMarker(),
      "",
      pathWithSearch(nextSearch),
    );
    notifySameDocumentSearchChange();
  }, []);

  useEffect(() => {
    if (
      parsedSelection.kind !== "invalid"
      && parsedView.kind !== "invalid"
      && !hasInsufficientOpenSelection
    ) return;
    const nextSearch = hasInsufficientOpenSelection
      ? removeComparisonView(window.location.search)
      : clearComparisonUrl(window.location.search);
    window.history.replaceState(
      stateWithoutComparisonMarker(),
      "",
      pathWithSearch(nextSearch),
    );
    notifySameDocumentSearchChange();
    setWriteError(hasInsufficientOpenSelection
      ? "The shared comparison view had too few videos and was closed."
      : "The shared comparison state was invalid and has been removed.");
  }, [hasInsufficientOpenSelection, parsedSelection.kind, parsedView.kind]);

  const setSelections = useCallback((nextSelections: readonly VideoSelection[]) => {
    try {
      const nextSearch = removeComparisonView(
        serializeVideoSelectionUrl(
          nextSelections,
          window.location.search,
        ),
      );
      replaceSearch(nextSearch);
      setWriteError(null);
      return true;
    } catch (caught) {
      if (!(caught instanceof VideoSelectionUrlError)) throw caught;
      setWriteError("These video selections are invalid or too large to share.");
      return false;
    }
  }, [replaceSearch]);

  const openComparison = useCallback(() => {
    const currentSelection = parseVideoSelectionUrl(window.location.search);
    const currentView = parseComparisonViewSearch(window.location.search);
    if (
      currentSelection.kind !== "valid"
      || currentSelection.selections.length < MINIMUM_VIDEO_COMPARISON_SELECTIONS
      || currentView.kind !== "absent"
    ) return;
    try {
      const params = new URLSearchParams(
        removeVideoDetailView(window.location.search),
      );
      params.set("compareView", "1");
      const nextSearch = serializeVideoSelectionUrl(
        currentSelection.selections,
        params,
      );
      window.history.pushState(
        { ...stateWithoutComparisonMarker(), [HISTORY_STATE_KEY]: OPEN_HISTORY_STATE },
        "",
        pathWithSearch(nextSearch),
      );
      setWriteError(null);
      notifySameDocumentSearchChange();
    } catch (caught) {
      if (!(caught instanceof VideoSelectionUrlError)) throw caught;
      setWriteError("These video selections are invalid or too large to share.");
    }
  }, []);

  const closeComparison = useCallback(() => {
    if (closingWithBackRef.current) return;
    const historyState = window.history.state as ComparisonHistoryState | null;
    if (historyState?.[HISTORY_STATE_KEY] === OPEN_HISTORY_STATE) {
      closingWithBackRef.current = true;
      window.history.back();
      return;
    }
    replaceSearch(removeComparisonView(window.location.search));
  }, [replaceSearch]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const historyState = window.history.state as ComparisonHistoryState | null;
    if (historyState?.[HISTORY_STATE_KEY] === OPEN_HISTORY_STATE) return;

    const openSearch = rawSearch(window.location.search);
    const closedSearch = removeComparisonView(openSearch);
    const baseState = stateWithoutComparisonMarker();
    window.history.replaceState(
      { ...baseState, [HISTORY_STATE_KEY]: CLOSED_HISTORY_STATE },
      "",
      pathWithSearch(closedSearch),
    );
    window.history.pushState(
      { ...baseState, [HISTORY_STATE_KEY]: OPEN_HISTORY_STATE },
      "",
      pathWithSearch(openSearch),
    );
    notifySameDocumentSearchChange();
  }, [isOpen]);

  return {
    clearSelections: () => setSelections([]),
    closeComparison,
    error,
    isOpen,
    openComparison,
    selections,
    setSelections,
  };
}
